import { z } from "zod"
import { union } from "lodash"

export function forceNonEmpty<T>(arr: T[]): T[] & { 0: T } {
  return arr as T[] & { 0: T }
}

export function getFirstOfNotEmptyArray<T>(arr: T[]) {
  return forceNonEmpty(arr)[0]
}

export function filterDefined<T>(arr: (T | undefined)[] | undefined): T[] {
  //Typescript has not fixed "Boolean() cannot be used to perform a null check" https://github.com/microsoft/TypeScript/issues/16655 yet
  if (!Array.isArray(arr)) return []
  return arr.filter((item) => item !== undefined) as T[]
}

export function filterNotUndefinedOrNull<T>(arr: (T | undefined | null)[]): T[] {
  //Typescript has not fixed "Boolean() cannot be used to perform a null check" https://github.com/microsoft/TypeScript/issues/16655 yet
  return arr.filter((item) => item !== undefined && item !== null) as T[]
}

export function uniq<T>(arr: readonly T[] | null | undefined): T[] {
  return [...new Set(arr)]
}

/** The distinct non-empty strings, sorted. */
export function distinctValues(values: readonly (string | null | undefined)[]): string[] {
  return uniq(values.filter((value): value is string => !!value)).sort()
}

/** Consecutive slices of at most `size` items; the last may be shorter. */
export function chunk<T>(arr: readonly T[], size: number): T[][] {
  if (!Number.isInteger(size) || size < 1) throw new Error(`chunk size must be a positive integer`)
  const chunks: T[][] = []
  for (let start = 0; start < arr.length; start += size) chunks.push(arr.slice(start, start + size))
  return chunks
}

/** Calls `fn` on the first call only and returns that result every time after */
export function once<T>(fn: () => T): () => T {
  let result: { value: T } | undefined
  return () => (result ??= { value: fn() }).value
}

export function getKeyByValue<T extends Record<string, any>>(object: T, value: any) {
  return Object.keys(object).find((key) => object[key] === value)
}

export function sort<T extends string | number | Date>(arr: T[], desc: boolean = false): T[] {
  if (desc) {
    return arr.sort((a, b) => (a > b ? -1 : 1))
  }
  return arr.sort((a, b) => (a > b ? 1 : -1))
}

export type ValueOf<T> = T[keyof T]

export function stringFromCamelCase(str: string): string {
  return str.split(/(?=[A-Z])/).join(" ")
}

export function isInt(n: string) {
  return /^[+-]?\d+$/.test(n)
}

export function smallest<T>(a: T, b: T) {
  return a < b ? a : b
}

export function largest<T>(...values: [T, ...T[]]): T {
  return values.reduce((max, current) => (current > max ? current : max), values[0])
}

export function sortByWeekDiff<T extends { weekDiff: number }>(orders: T[]): T[] {
  return orders.length > 1 ? orders.toSorted((a, b) => a.weekDiff - b.weekDiff) : orders
}

export function mapBy<T, K extends keyof T>(a: readonly T[], k: K): Map<T[K], T> {
  return new Map(a.map((a_) => [a_[k], a_]))
}

/** Items whose `k` is in `keys`, in `keys` order; keys with no item are skipped. */
export function pickByKey<T, K extends keyof T>(a: readonly T[], k: K, keys: readonly T[K][]): T[] {
  const byKey = mapBy(a, k)
  return filterDefined(keys.map((key) => byKey.get(key)))
}

export function sumNumberArrays(arrays: number[][]) {
  if (!arrays || arrays.length === 0) return []
  if (arrays.length === 1) return arrays[0] ?? []

  const result: number[] = new Array(arrays[0]?.length).fill(0)

  for (const arr of arrays) {
    for (let i = 0; i < arr.length; i++) {
      result[i] = (arr[i] ?? 0) + (result[i] ?? 0)
    }
  }

  return result
}

type NumberKeys<T> = {
  [K in keyof T]: T[K] extends number ? K : never
}

export function sumBy<T>(arr: T[], k: keyof NumberKeys<T> | ((item: T) => number)): number {
  let sum = 0
  if (typeof k === "function") {
    for (const item of arr) sum += k(item)
  } else {
    // Lodash sumBy does not have proper type checking
    for (const item of arr) {
      // @ts-ignore fix this. There is no real problem since T[K] needs to be a number
      sum += item[k] ?? 0
    }
  }
  return sum
}

type StringOrNumberKeys<T> = {
  [K in keyof T]: T[K] extends string | number ? K : never
}[keyof T]

export function groupBy<T>(
  array: T[] | null | undefined,
  keyFn: (item: T) => string | number,
): Record<string, T[]>
export function groupBy<T>(
  array: T[] | null | undefined,
  prop: StringOrNumberKeys<T>,
): Record<string, T[]>
export function groupBy<T>(
  array: T[] | null | undefined,
  key: ((item: T) => string | number) | StringOrNumberKeys<T>,
): Record<string, T[]> {
  const acc: Record<string, T[]> = {}
  if (!array) return acc
  if (typeof key === "function") {
    for (const item of array) (acc[key(item)] ??= []).push(item)
  } else {
    // @ts-ignore StringOrNumberKeys guarantees T[key] is string | number
    for (const item of array) (acc[item[key]] ??= []).push(item)
  }
  return acc
}

export function groupByMany<T>(arr: T[], key: (input: T) => string[]) {
  const out: Record<string, T[]> = {}
  for (const item of arr) {
    for (const groupKey of key(item)) {
      ;(out[groupKey] ??= []).push(item)
    }
  }
  return out
}

// In order to ensure that we have static references, we use
// empty arrays,because otherwise each render will create a new array
// which might cause the memoization to fail
export const EMPTY_ARRAY = []
Object.freeze(EMPTY_ARRAY)

export function getZodEnumFromObjectKeys<
  // Stolen from https://github.com/colinhacks/zod/discussions/839#discussioncomment-8142768
  TI extends Record<string, any>,
  R extends string = TI extends Record<infer R, any> ? R : never,
>(input: TI): z.ZodEnum<[R, ...R[]]> {
  const [firstKey, ...otherKeys] = Object.keys(input) as [R, ...R[]]
  return z.enum([firstKey, ...otherKeys])
}

export function mergeMap<K, V>(oldData: Map<K, V> | undefined, newData: Map<K, V>): Map<K, V> {
  if (!oldData) {
    return new Map(newData)
  }

  const updatedMap = new Map(oldData)

  newData.forEach((value, key) => {
    updatedMap.set(key, value)
  })

  return updatedMap
}

export function mergeMapsSimple<K, V>(map1: Map<K, V[]>, map2: Map<K, V[]>): Map<K, V[]> {
  const allKeys = union(Array.from(map1.keys()), Array.from(map2.keys()))

  return new Map(
    allKeys.map((key) => [key, [...(map1.get(key as K) || []), ...(map2.get(key as K) || [])]]),
  )
}

export const dynamicParse = (value: string | number | null | undefined): string | number => {
  const floatMatchRegex = /^-{0,1}\d*[,.]{1}\d*$/g
  const intMatchRegex = /^-{0,1}\d*$/g
  if (value === null || value === undefined) {
    return 0
  }
  if (typeof value === "number") {
    return value
  }
  if (typeof value === "string") {
    if (value === "") {
      return 0 //Interpret empty strings as the number 0
    }
    if (floatMatchRegex.test(value)) {
      return parseFloat(value)
    }
    if (intMatchRegex.test(value)) {
      return parseInt(value, 10)
    }
    return value
  }
  if (typeof value === "boolean") {
    return `${value}`
  }
  if (typeof value === "object") {
    throw new Error(`Unable to dynamically parse object ${JSON.stringify(value)}`)
  }
  throw new Error(
    `Error during dynamic parsing, unable to parse value ${value} of type ${typeof value}`,
  )
}
